import { isString } from 'lodash'
import { ChangeEvent, ReactElement, ReactNode, MouseEvent, useRef } from 'react'
import { Accept, useDropzone } from 'react-dropzone'
import styled from 'styled-components'

import excelFileImg from 'assets/images/excel-file-image.svg'
import fileImg from 'assets/images/file-image.svg'
import pdfFileImg from 'assets/images/pdf-file-image.svg'
import wordFileImg from 'assets/images/word-file-image.svg'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { CircleCloseIcon } from 'components/Icons/CircleCloseIcon'
import { Label, OptionalLabel } from 'components/v2/Label'
import { FormattedMessage } from 'react-intl'
import { UploadIcon } from 'components/Icons/UploadIcon'
import { getFileName } from 'utils/file'

export const KB = 1024
export const MB = 1024 * 1024

const InputFileWrapper = styled.div`
  display: block;
  width: 100%;
`

const CustomInputFile = styled.div<{ disabled?: boolean; isDragAccept?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  padding: 16px 10px;
  color: #b0b0b0;
  font-weight: 500;
  border-radius: 8px;
  border: 1px dashed #8f8bff;
  cursor: ${({ disabled }): string => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }): string => (disabled ? '0.5' : '1')};
  font-size: 12px;
  background-color: ${({ isDragAccept }): string => (isDragAccept ? '#8f8bff20' : 'transparent')};

  strong {
    color: #5d59d7;
    font-weight: 500;
    text-decoration: underline;
  }
`

export interface IInputFileProps {
  touched?: boolean
  errors?: string
  placeholder?: ReactNode
  label?: ReactNode
  onChange: (file: File | '') => void
  value?: File | null | string
  inputProps?: React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
  optional?: boolean
  accept?: Accept
  minSize?: number
  maxSize?: number
  input?: ReactNode
}

export const InputFile = ({
  touched,
  errors,
  placeholder,
  label,
  onChange,
  optional,
  inputProps = {},
  accept,
  minSize,
  maxSize,
  input
}: IInputFileProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null)
  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    accept,
    minSize,
    maxSize,
    onDrop: (files) => {
      handleFiles(files as unknown as FileList)
    }
  })

  const handleChange = ({ target: { files } }: ChangeEvent<HTMLInputElement>): void => {
    handleFiles(files)
  }

  const handleFiles = (files: FileList | null): void => {
    if (files && files.length > 0) {
      onChange(files[0])

      if (inputRef.current) {
        inputRef.current.value = ''
      }
    }
  }

  const showError = touched && Boolean(errors)

  return (
    <div>
      {Boolean(label) && (
        <Label>
          {label}{' '}
          {optional && (
            <OptionalLabel>
              <FormattedMessage id="form.optional.label" defaultMessage="Optional" />
            </OptionalLabel>
          )}
        </Label>
      )}
      <InputFileWrapper {...getRootProps()}>
        {input ? (
          <>{input}</>
        ) : (
          <CustomInputFile isDragAccept={isDragAccept}>
            <UploadIcon />
            <div>
              <FormattedMessage
                id="inputFiles.placeholder"
                defaultMessage="Drag your file or click <strong>here</strong> to upload {placeholder}"
                values={{
                  placeholder: placeholder ?? '',
                  strong: (msg) => <strong>{msg}</strong>
                }}
              />
            </div>
          </CustomInputFile>
        )}
        <input
          type="file"
          onChange={handleChange}
          style={{ display: 'none' }}
          {...inputProps}
          ref={inputRef}
          {...getInputProps()}
        />
      </InputFileWrapper>
      {showError && <ErrorMessage>{errors}</ErrorMessage>}
    </div>
  )
}

const FilePreviewContainer = styled.a`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: space-between;
  padding: 16px 8px;
  border-radius: 8px;
  border: 1px dashed #e3e3e3;
  text-decoration: none;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const FileName = styled.div`
  color: #8f8bff;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`

const CloseIconContainer = styled.div`
  display: flex;
  cursor: pointer;

  svg {
    fill: #b0b0b0;
  }
`

const Image = styled.img`
  width: 20px;
  height: 20px;
`

export const ExtensionImg = ({ filename }: { filename: string }): ReactElement => {
  const fileExtension = filename.split('.').pop()

  if (!fileExtension) {
    return <></>
  }

  let src: string
  switch (fileExtension.toLowerCase()) {
    case 'pdf':
      src = pdfFileImg
      break
    case 'doc':
    case 'docx':
      src = wordFileImg
      break
    case 'csv':
    case 'xls':
    case 'xlsx':
      src = excelFileImg
      break
    default:
      src = fileImg
  }

  return <Image src={src} alt="" />
}
export const FilePreview = ({
  file,
  filename,
  onRemoveFile
}: {
  file: File | string
  filename?: string
  onRemoveFile?: () => void
}): ReactElement => {
  const handleRemoveSelectedFile = (event: MouseEvent<HTMLDivElement>): void => {
    event.preventDefault()
    event.stopPropagation()

    onRemoveFile?.()
  }

  const displayedFileName = filename || getFileName(file)

  return (
    <FilePreviewContainer href={isString(file) ? file : undefined} download={isString(file)}>
      <ExtensionImg filename={displayedFileName} />
      <FileName>{displayedFileName}</FileName>
      {!!onRemoveFile && (
        <CloseIconContainer onClick={handleRemoveSelectedFile}>
          <CircleCloseIcon />
        </CloseIconContainer>
      )}
    </FilePreviewContainer>
  )
}

export const InputFileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
